import { TablePagination } from '@edsn/pie-components';
import arrowDropDownIcon from '@iconify/icons-material-symbols/arrow-drop-down-rounded';
import arrowDropUpIcon from '@iconify/icons-material-symbols/arrow-drop-up-rounded';
import type {
  PaginationState,
  Row,
  RowData,
  RowSelectionState,
  SortingState,
  TableOptions,
  Updater,
} from '@tanstack/react-table';
import {
  flexRender,
  functionalUpdate,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { ComponentProps, ReactNode } from 'react';
import { useState } from 'react';
import { Link } from 'react-router';
import type { Optional } from '#pie/types';
import { ConditionalWrapper } from '#pie/utils/ConditionalWrapper';
import { cn, tv } from '#pie/utils/TailwindUtils';
import { Icon } from '../icon/Icon';
import { InputCheckbox } from '../input-checkbox/InputCheckbox';
import { OverflowText } from '../overflow-text/OverflowText';
import { Skeleton } from '../skeleton/Skeleton';
import { Tooltip } from '../tooltip/Tooltip';

export interface TableProps<T extends RowData>
  extends Optional<
    Omit<
      TableOptions<T>,
      'getCoreRowModel' | 'manualPagination' | 'enableGrouping' | 'state'
    >,
    'data'
  > {
  className?: string;
  emphasizedRows?: string[];
  pagination: PaginationState;
  pageSizes?: number[];
  sorting: SortingState;
  rowSelection?: RowSelectionState;
  totalCount?: number;
  isLoading?: boolean | number;
  label?: string;
  selectionDisabledMessage?: ReactNode;

  rowButton?: (row: Row<T>) => ComponentProps<'button'>;
  rowLinks?: (row: Row<T>) => ComponentProps<typeof Link>;
}

export const useTableState = ({
  pageSizes,
  defaultSort,
  defaultPagination,
}: {
  pageSizes: number[];
  defaultSort?: SortingState;
  defaultPagination?: PaginationState;
}) => {
  const [pagination, setPagination] = useState<PaginationState>(
    defaultPagination || {
      pageIndex: 0,
      pageSize: Math.min(...pageSizes),
    },
  );
  const [sorting, setSorting] = useState<SortingState>(defaultSort || []);

  const onPaginationChange = (paginationUpdater: Updater<PaginationState>) => {
    const newPaginationVal = functionalUpdate(paginationUpdater, pagination);
    setPagination(newPaginationVal);
  };

  const onSortingChange = (sortingUpdater: Updater<SortingState>) => {
    const newSortingVal = functionalUpdate(sortingUpdater, sorting);
    setSorting(newSortingVal);
  };

  return {
    onPaginationChange,
    onSortingChange,
    pagination,
    sorting,
  };
};

export function Table<T extends RowData>({
  data,
  emphasizedRows = [],
  pageSizes,
  pagination,
  rowSelection,
  sorting,
  totalCount,
  isLoading,
  label,
  selectionDisabledMessage,
  rowButton,
  rowLinks,
  ...props
}: TableProps<T>) {
  const styles = tableStyles();

  const tableInstance = useReactTable({
    data: data ?? [],
    enableGrouping: false,
    enableRowSelection: !!props.onRowSelectionChange && !!rowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    state: {
      pagination,
      sorting,
      ...(rowSelection ? { rowSelection } : {}),
    },
    ...props,
  });

  const smallestPageSize = pageSizes ? Math.min(...pageSizes) : 10;

  const pageRows = tableInstance.getRowModel().rows;
  const isSelectAllDisabled =
    pageRows.length > 0 && !pageRows.some((row) => row.getCanSelect());

  const withDisabledSelectionTooltip = (
    checkbox: ReactNode,
    isDisabled: boolean,
  ) =>
    isDisabled && selectionDisabledMessage ? (
      <Tooltip message={selectionDisabledMessage} side="right" variant="dark">
        <span className="inline-flex [&_button]:pointer-events-none">
          {checkbox}
        </span>
      </Tooltip>
    ) : (
      checkbox
    );

  return (
    <div className={styles.container()}>
      <div className={styles.tableWrapper()}>
        <table className={styles.table()} aria-label={label}>
          <thead>
            {tableInstance.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {!!props.onRowSelectionChange && (
                  <th className={styles.td()}>
                    {withDisabledSelectionTooltip(
                      <InputCheckbox
                        checked={
                          tableInstance.getIsAllPageRowsSelected()
                            ? true
                            : tableInstance.getIsSomePageRowsSelected()
                              ? 'indeterminate'
                              : false
                        }
                        disabled={isSelectAllDisabled}
                        onCheckedChange={(checked) =>
                          tableInstance.getToggleAllPageRowsSelectedHandler()({
                            target: { checked },
                          })
                        }
                      />,
                      isSelectAllDisabled,
                    )}
                  </th>
                )}
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className={styles.th()}>
                    {!!props.onSortingChange && (
                      <button
                        type="button"
                        className="flex flex-row text-left"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {{
                          asc: (
                            <Icon
                              icon={arrowDropUpIcon}
                              className={styles.sorting()}
                            />
                          ),
                          desc: (
                            <Icon
                              icon={arrowDropDownIcon}
                              className={styles.sorting()}
                            />
                          ),
                        }[header.column.getIsSorted() as string] ?? (
                          <Icon icon={arrowDropDownIcon} className="ml-2" />
                        )}
                      </button>
                    )}
                    {!props.onSortingChange &&
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading
              ? new Array(
                  typeof isLoading === 'number'
                    ? isLoading
                    : pagination.pageSize,
                )
                  .fill(null)
                  .map((_, rowIndex) => (
                    <tr
                      key={`row${rowIndex}`}
                      className={cn(
                        styles.base({ isChecked: false }),
                        styles.tr(),
                      )}
                    >
                      {!!props.onRowSelectionChange && (
                        <td className={styles.td()}>
                          <InputCheckbox disabled />
                        </td>
                      )}
                      {tableInstance
                        .getHeaderGroups()[0]
                        .headers.map((_, cellIndex) => (
                          <td key={`cell${cellIndex}`} className={styles.td()}>
                            <Skeleton />
                          </td>
                        ))}
                    </tr>
                  ))
              : tableInstance.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    data-testid={`table-row-${row.id}`}
                    className={cn(
                      styles.base({
                        emphasis: emphasizedRows.includes(row.id),
                        isChecked: row.getIsSelected(),
                      }),
                      styles.tr(),
                    )}
                  >
                    {!!props.onRowSelectionChange && (
                      <td className={cn(styles.td(), 'relative z-10')}>
                        {withDisabledSelectionTooltip(
                          <InputCheckbox
                            checked={row.getIsSelected()}
                            disabled={!row.getCanSelect()}
                            onCheckedChange={(checked) =>
                              row.getToggleSelectedHandler()({
                                target: { checked },
                              })
                            }
                          />,
                          !row.getCanSelect(),
                        )}
                      </td>
                    )}
                    {row.getVisibleCells().map((cell, i) => {
                      // NOTE: White columnWidth is defined in a module declaration file the pipeline fails when trying to use it, hence this workaround

                      const columnWidth = (cell.column.columnDef.meta as any)
                        ?.columnWidth;
                      const rowButtonProps = rowButton?.(row);
                      return (
                        <td
                          key={cell.id}
                          className={cn(
                            styles.td(),
                            i ===
                              cell.getContext().row.getVisibleCells().length -
                                1 && 'w-full',
                          )}
                        >
                          {i === 0 && rowButtonProps && (
                            <button
                              type="button"
                              {...rowButtonProps}
                              className={cn(
                                'absolute inset-0 cursor-pointer',
                                rowButtonProps.className,
                              )}
                            />
                          )}
                          {i === 0 && rowLinks && (
                            <Link
                              {...rowLinks(row)}
                              className="absolute inset-0"
                            />
                          )}
                          <ConditionalWrapper
                            condition={!!columnWidth}
                            wrapper={(children) => (
                              <OverflowText width={columnWidth}>
                                {children}
                                {rowLinks && (
                                  <Link
                                    {...rowLinks(row)}
                                    className="absolute inset-0"
                                  />
                                )}
                              </OverflowText>
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </ConditionalWrapper>
                        </td>
                      );
                    })}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      {!!props.onPaginationChange &&
        ((isLoading && typeof isLoading === 'number'
          ? isLoading >= smallestPageSize
          : isLoading) ||
          (totalCount || 0) > smallestPageSize) && (
          <TablePagination
            currentPage={pagination.pageIndex + 1}
            pageSize={pagination.pageSize}
            total={totalCount ?? 0}
            pageSizeOptions={pageSizes}
            onPageChange={(page) =>
              props.onPaginationChange?.((old) => ({
                ...old,
                pageIndex: page - 1,
              }))
            }
            onPageSizeChange={(size) =>
              props.onPaginationChange?.((old) => ({
                ...old,
                pageSize: size,
                pageIndex: 0,
              }))
            }
          />
        )}
    </div>
  );
}

export const tableStyles = tv({
  base: '',
  slots: {
    container: 'bg-fit rounded-xs space-y-4',
    sorting:
      'bg-surface-minimal-teal rounded-full ml-2 text-action-primary-enabled',
    table: 'min-w-full',
    tableWrapper: 'overflow-x-auto border border-border-minimal-enabled',
    td: 'py-2 px-3 whitespace-nowrap',
    th: 'text-left py-2 px-3 whitespace-nowrap text-sm font-bold',
    tr: 'hover:bg-surface-minimal-gray h-[48px] relative border-l border-l-[transparent] last:border-b-0',
  },
  variants: {
    emphasis: {
      true: 'font-bold',
    },
    isChecked: {
      false: 'border-y border-border-minimal-enabled',
      true: {
        base: 'bg-surface-minimal-teal hover:bg-surface-minimal-teal border border-double border-border-subtle-enabled',
        tr: 'border-l-action-primary-enabled',
      },
    },
  },
});
